import { useState } from 'react'

/**
 * A value a host may control: the prop wins when it is set, otherwise local state does. The
 * setter always updates local state and then reports through `onChange`, so the component still
 * works standalone (e.g. in tests) while a controlling host hears every change.
 *
 * The setter is a fresh function every render (it closes over the current `onChange`), so a
 * caller must not memoize around it with stale deps.
 */
export function useControllableValue<T>(
  value: T | undefined,
  onChange: ((next: T) => void) | undefined,
  initialValue: T
): [T, (next: T) => void] {
  const [localValue, setLocalValue] = useState<T>(initialValue)
  const currentValue = value ?? localValue

  function setValue(next: T) {
    setLocalValue(next)
    onChange?.(next)
  }

  return [currentValue, setValue]
}
